import {
  checkFilterQuerySync,
  getExcludeKeySuggestion,
  getFilterBlockChunkByCaret,
  getFilterValue,
  getProviderKey,
  getUnescapedFilterValue,
  isFilterBlock,
  isIndexedFilterBlock,
  isIndexedTextBlock,
  sanitizeOperators,
} from './utils'
import {
  BlockType,
  type ARIAFilterSuggestion,
  type AnyBlock,
  type FilterBlock,
  type FilterConfig,
  type FilterProvider,
  ProviderSupportStatus,
  FILTER_PRIORITY_DISPLAY_THRESHOLD,
  FilterValueType,
  type QueryContext,
} from './types'
import {Strings} from './constants/strings'
import {fuzzyScore} from '@github-ui/fuzzy-score/fuzzy-score'
import {hasMatch} from 'fzy.js'
import {defaultFilterConfig} from './constants/defaults'

type TransferFilterQueryContext = {
  cachedFilterCount?: number
  staticContext?: Record<string, string>
}

export class FilterQuery {
  activeBlockId: number
  cachedFilterCount: number = 0
  config: FilterConfig
  isValidated: boolean = false
  #blocks: AnyBlock[]
  #staticContext?: Record<string, string> = {}
  #errors: string[] = []
  #raw: string
  #valid: boolean

  constructor(
    raw: string = '',
    blocks: AnyBlock[] = [],
    config: FilterConfig = defaultFilterConfig,
    activeBlockId = -1,
    instanceContext?: TransferFilterQueryContext,
    isValidated = false,
  ) {
    this.#blocks = blocks
    this.#raw = raw
    this.isValidated = isValidated
    if (activeBlockId !== undefined) {
      this.activeBlockId = activeBlockId
    }
    if (instanceContext) {
      this.#staticContext = instanceContext.staticContext
      this.cachedFilterCount = instanceContext.cachedFilterCount ?? 0
      this.config = config
    }
    if ((raw.match(/"/g) ?? []).length % 2 !== 0) {
      this.#valid = false
    } else {
      this.#valid = true
      this.#processErrorBlocks(blocks)
    }
  }

  get activeBlock() {
    return this.blocks[this.activeBlockId] ?? null
  }

  get blocks() {
    return this.#blocks
  }

  get context(): QueryContext {
    const context: QueryContext = this.#staticContext ?? {}
    if (!this.#staticContext?.['repo']) {
      const repoBLocks = this.#blocks.filter(b => isFilterBlock(b) && b.provider.key === 'repo') as FilterBlock[]
      const repos = repoBLocks
        .map(r => r.value.values.map(v => (v.valid !== false ? getFilterValue(v.value) ?? '' : '')))
        .flat()
      if (repos.length > 0) context['repo'] = repos.join(',')

      if (!this.#staticContext?.['org']) {
        const orgBlocks = this.#blocks.filter(b => isFilterBlock(b) && b.provider.key === 'org') as FilterBlock[]
        const orgs = orgBlocks
          .map(r => r.value.values.map(v => (v.valid !== false ? getFilterValue(v.value) ?? '' : '')))
          .flat()

        if (orgs.length > 0) context['org'] = orgs.join(',')
      }
    }

    return context
  }

  get contextURLParams() {
    return new URLSearchParams(this.context)
  }

  get errors() {
    return this.#errors ? Array.from(this.#errors) : []
  }

  get filterCount() {
    if (!this.isValidated || !checkFilterQuerySync(this)) return this.cachedFilterCount
    const updatedCount = this.blocks.filter(b => isFilterBlock(b) && b.value.values.some(v => v.valid)).length
    this.cachedFilterCount = updatedCount
    return updatedCount
  }

  get instanceContext() {
    return {
      cachedFilterCount: this.cachedFilterCount,
      staticContext: this.#staticContext,
    }
  }

  get isValid() {
    return this.#valid
  }

  get raw() {
    return this.#raw
  }

  get staticContext() {
    return this.#staticContext
  }

  set staticContext(staticContext: QueryContext | undefined) {
    this.#staticContext = staticContext
  }

  async getSuggestions(
    caretIndex: number,
    filterProviders: Record<string, FilterProvider>,
    filterConfig: FilterConfig,
  ) {
    const activeBlock = this.activeBlock

    // later on, we may want to combine these into an array of objects
    // to allow for grouped suggestions with optional headings
    // https://github.com/github/github/pull/315881#discussion_r1515334291
    let aggregatedSuggestions: ARIAFilterSuggestion[] = []
    /** FIRST CASE: Show recommended providers
     * If there is no active block and the query is empty
     * If the active block is a space
     */
    if ((!activeBlock && this.raw === '') || activeBlock?.type === BlockType.Space) {
      const providers = Object.values(filterProviders)
        /** Filter out providers that:
         *  - Are not multikey and already have a block in the query
         *  - Are not supported
         */
        .filter(provider => {
          return (
            (provider.options.filterTypes?.multiKey !== false ||
              (provider.options.filterTypes?.multiKey === false &&
                this.blocks.filter(block => isFilterBlock(block) && block.key.value === provider.key).length < 1)) &&
            provider.options.support.status === ProviderSupportStatus.Supported
          )
        })
        /** Filter by priority and then sort by display name */
        .filter(provider => provider.priority <= FILTER_PRIORITY_DISPLAY_THRESHOLD)
        .sort((a, b) => a.priority - b.priority)
        .map(
          (provider): ARIAFilterSuggestion => ({
            type: FilterValueType.Key,
            displayName: provider.displayName,
            ariaLabel: `${provider.displayName}, Filter, ${provider.description}`,
            priority: provider.priority,
            icon: provider.icon,
            value: provider.key,
          }),
        )

      // Take only the first 8 providers
      aggregatedSuggestions = providers

      // an item can be exclusive:true, but also be multiKey:false, which would mean that if that block is already in the query, it can't be inserted again and thus would take it off the Exclusions list too
      const showExcludeSuggestions = (provider: FilterProvider) => {
        return (
          provider.options.filterTypes.exclusive === true &&
          (provider.options.filterTypes?.multiKey !== false ||
            (provider.options.filterTypes?.multiKey === false &&
              this.blocks.filter(block => isFilterBlock(block) && block.key.value === provider.key).length < 1))
        )
      }
      if (Object.values(filterProviders).filter(showExcludeSuggestions).length > 0) {
        aggregatedSuggestions.push(getExcludeKeySuggestion())
      }
    } else if (activeBlock?.type === BlockType.Text && activeBlock.raw === '-') {
      /** SECOND CASE: Show providers that support exclusions
       * If the active block is a text block and the raw value is a dash (indicating a potential exclusive filter)
       */
      const providers = Object.values(filterProviders)
        /** Filter out providers that:
         *  - Support exclusions
         *  - Are supported
         */
        .filter(provider => {
          return (
            provider.options.filterTypes.exclusive &&
            provider.options.support.status === ProviderSupportStatus.Supported
          )
        })
        /** Filter by priority and then sort by display name */
        .filter(provider => provider.priority <= FILTER_PRIORITY_DISPLAY_THRESHOLD)
        .sort((a, b) => a.priority - b.priority)
        .map(
          (provider): ARIAFilterSuggestion => ({
            type: FilterValueType.Key,
            displayName: provider.displayName,
            ariaLabel: `${provider.displayName}, Filter, ${provider.description}`,
            priority: provider.priority,
            icon: provider.icon,
            value: `-${provider.key}`,
          }),
        )
      // Take only the first 8 providers
      aggregatedSuggestions = providers
    } else if (activeBlock?.type === BlockType.Text) {
      /** THIRD CASE: Show filtered providers
       * If the active block is a text block
       */
      const matches: ARIAFilterSuggestion[] = []

      const textBlock = isIndexedTextBlock(activeBlock) ? activeBlock : null
      const matchFilterTo =
        textBlock && caretIndex - textBlock.startIndex <= textBlock.raw.indexOf(filterConfig.filterDelimiter)
          ? textBlock.raw.substring(0, caretIndex - textBlock.startIndex)
          : activeBlock.raw

      Object.values(filterProviders).map(provider => {
        /**
         * If the activeBlock raw text partially matches the providers key
         * If the activeBlock raw text partially matches the providers display name, if it's available
         * If alias matching is turned on and it matches one of the provider's aliases
         * If the provider supports exclusive and the raw text partially matches the provider key with a dash
         * If the provider supports exclusive and the raw text partially matches one of the provider's aliases with a dash
         * If the provider supports multikey
         * If the provider doesn't support multikey and there is not already a block with this key in the query
         * If the provider is supported
         */
        if (
          (provider.key.startsWith(matchFilterTo) ||
            (provider.displayName && provider.displayName.startsWith(matchFilterTo)) ||
            (filterConfig.aliasMatching && provider.aliases?.some(a => a.startsWith(matchFilterTo))) ||
            (provider.options.filterTypes.exclusive &&
              (`-${provider.key}`.startsWith(matchFilterTo) ||
                (filterConfig.aliasMatching && provider.aliases?.some(a => `-${a}`.startsWith(matchFilterTo)))))) &&
          (provider.options.filterTypes.multiKey !== false ||
            this.blocks.filter(block => isFilterBlock(block) && block.key.value === provider.key).length < 1) &&
          provider.options.support.status === ProviderSupportStatus.Supported
        ) {
          let value = provider.key
          // "Exclusion" mode, meaning user has started with a dash
          if (activeBlock.raw.startsWith('-') && provider.options.filterTypes.exclusive) {
            value = `-${provider.key}`
          }
          matches.push({
            type: FilterValueType.Key,
            displayName: provider.displayName,
            ariaLabel: `${provider.displayName}, Filter, ${provider.description}`,
            priority: provider.priority - fuzzyScore(matchFilterTo, provider.key),
            icon: provider.icon,
            value,
          })
        }
      })

      // Priority-sort the list and take the first 8
      matches.sort((a, b) => a.priority - b.priority)
      aggregatedSuggestions = matches
    } else if (activeBlock && isFilterBlock(activeBlock)) {
      /*^ FOURTH CASE: It's complicated
       * If the active block is a filter block
       * This case is usually for when a block has been added and the user goes back to edit later
       */
      let filteredSuggestions: ARIAFilterSuggestion[] = []
      const filterKey = getProviderKey(activeBlock.key.value)
      // Check to make sure the filter block has indexes we can check to determine where the caret is
      if (isIndexedFilterBlock(activeBlock)) {
        // Get the chunk of the filter block the caret is in: key, value
        const [type, chunk] = getFilterBlockChunkByCaret(activeBlock, caretIndex)
        // If the caret is in the key, show provider suggestions
        if (type === 'key') {
          const matches: ARIAFilterSuggestion[] = []
          Object.values(filterProviders).map(provider => {
            if (
              (hasMatch(chunk.value, provider.key) ||
                (provider.options.filterTypes.exclusive && hasMatch(chunk.value, `-${provider.key}`))) &&
              (provider.options.filterTypes.multiKey !== false ||
                this.blocks.filter(block => isFilterBlock(block) && block.key.value === provider.key).length < 1)
            ) {
              matches.push({
                type: FilterValueType.Key,
                displayName: provider.displayName,
                ariaLabel: `${provider.displayName}, Filter, ${provider.description}`,
                priority: provider.priority - fuzzyScore(chunk.value, provider.key),
                icon: provider.icon,
                value: provider.key,
              })
            }
          })
          matches.sort((a, b) => a.priority - b.priority)
          aggregatedSuggestions = matches
          // If the caret is in the value, show value suggestions for the block's provider
        } else if (type === 'value') {
          const provider =
            filterProviders[filterKey] ??
            (filterConfig.aliasMatching
              ? Object.values(filterProviders).find(p => p.aliases?.includes(filterKey))
              : null)

          const providerSuggestions = await provider?.getSuggestions(this, activeBlock, filterConfig, caretIndex)

          // Filter out suggestions from this block
          filteredSuggestions =
            providerSuggestions
              ?.filter(
                suggestion =>
                  activeBlock.value.values.findIndex(
                    value => getUnescapedFilterValue(value.value) === suggestion.value && !value.hasCaret,
                  ) < 0,
              )
              .sort((a, b) => a.priority - b.priority) ?? []
        }
      }
      aggregatedSuggestions = filteredSuggestions
    }

    return aggregatedSuggestions
  }

  #processErrorBlocks(blocks: AnyBlock[]) {
    const errorSet = new Set<string>()
    for (const block of blocks.filter(b => isFilterBlock(b) && b.valid === false && this.activeBlockId !== b.id)) {
      if ((block as FilterBlock).validationMessage) errorSet.add((block as FilterBlock).validationMessage ?? '')
      if (!(block as FilterBlock).key.valid) errorSet.add((block as FilterBlock).key.validationMessage ?? '')
      for (const value of (block as FilterBlock).value.values) {
        if (value.valid !== false) continue
        if (!value.value) {
          errorSet.add(Strings.filterValueEmpty((block as FilterBlock).key.value))
        } else if (value.validationMessage) {
          errorSet.add(value.validationMessage)
        } else {
          errorSet.add(
            Strings.filterInvalidValue(
              (block as FilterBlock).key.value,
              sanitizeOperators(getFilterValue(value.value)) ?? '',
            ),
          )
        }
      }
    }
    this.#errors = Array.from(errorSet)
  }
}
